import type { User, LoginPayload, RegisterPayload } from '#shared/types/user'

export function useAuth() {
  // useState (não ref de módulo): no SSR em Cloudflare Workers o isolate é
  // reaproveitado entre requisições de usuários diferentes — um `ref` no
  // escopo do módulo vira estado global compartilhado entre eles. useState
  // é isolado por requisição.
  const user = useState<User | null>('auth-user', () => null)
  const loading = useState('auth-loading', () => false)
  const config = useRuntimeConfig()
  const router = useRouter()
  const { identifyUser, trackEvent } = useUmami()

  async function refreshSession() {
    const data = await $fetch<User>('/auth/refresh', {
      method: 'POST',
      baseURL: config.public.apiBaseUrl,
      credentials: 'include',
    })
    user.value = data
    return data
  }

  async function fetchUser() {
    try {
      const data = await $fetch<User>('/auth/me', {
        baseURL: config.public.apiBaseUrl,
        credentials: 'include',
      })
      user.value = data
    }
    catch {
      try {
        await refreshSession()
      }
      catch {
        user.value = null
      }
    }
  }

  async function login(payload: LoginPayload) {
    loading.value = true
    try {
      const data = await $fetch<User>('/auth/login', {
        method: 'POST',
        baseURL: config.public.apiBaseUrl,
        body: payload,
        credentials: 'include',
      })
      user.value = data
      identifyUser(data.id, { role: data.role })
      trackEvent('auth-login', { role: data.role })
      const route = useRoute()
      const redirect = route.query.redirect as string | undefined
      await router.push(redirect && redirect.startsWith('/') ? redirect : '/')
    }
    finally {
      loading.value = false
    }
  }

  async function register(payload: RegisterPayload) {
    loading.value = true
    try {
      const data = await $fetch<User>('/auth/register', {
        method: 'POST',
        baseURL: config.public.apiBaseUrl,
        body: payload,
        credentials: 'include',
      })
      user.value = data
      identifyUser(data.id, { role: data.role })
      trackEvent('auth-register', { role: data.role })
      await router.push('/')
    }
    finally {
      loading.value = false
    }
  }

  async function logout() {
    await $fetch('/auth/logout', {
      method: 'POST',
      baseURL: config.public.apiBaseUrl,
      credentials: 'include',
    }).catch(() => {})
    trackEvent('auth-logout', { role: user.value?.role ?? 'anonymous' })
    user.value = null
    // Hard navigation: garante reset completo do estado Nuxt e re-execução do plugin auth.
    // router.push('/') falha silenciosamente quando já está em '/' (NavigationDuplicated),
    // deixando o header desatualizado.
    window.location.href = '/'
  }

  const isAdmin = computed(() => user.value?.role === 'ADMIN')
  const isModerator = computed(() => user.value?.role === 'MODERATOR' || isAdmin.value)

  return { user: readonly(user), loading: readonly(loading), isAdmin, isModerator, fetchUser, refreshSession, login, register, logout }
}
